import React from "react";
import {
  ArrowRight,
  Award,
  BookOpen,
  Briefcase,
  GraduationCap,
  HandHeart,
  Heart,
  Megaphone,
  Sparkles,
  Users,
} from "lucide-react";
import { PageHero, SectionHeader, EmeraldButton } from "@/components/Bits";
import { FOUNDER_PORTRAIT, FOUNDER_PORTRAIT_ALT, LIFESTYLE } from "@/lib/brand";

const credentials = [
  { icon: Briefcase, kicker: "26+ Years", title: "Federal Service Experience" },
  { icon: GraduationCap, kicker: "PhD", title: "Public Health (Epidemiology)" },
  { icon: Heart, kicker: "Certified", title: "Health Coach" },
  { icon: Award, kicker: "Certified", title: "Master Life Coach" },
  { icon: Users, kicker: "Professional", title: "Administrative Management Specialist" },
  { icon: Sparkles, kicker: "Entrepreneur", title: "Digital Creator & Founder" },
  { icon: Megaphone, kicker: "Speaker", title: "Professional Development Educator" },
  { icon: HandHeart, kicker: "Mission-Driven", title: "Advocate for Economic Empowerment" },
];

const values = [
  {
    title: "Authenticity & Integrity",
    body: "We grow with transparency. Our communications and commitments are honest reflections of where we are and where we're going.",
  },
  {
    title: "Education at the Core",
    body: "Our work is rooted in research, lived experience, and a generous transfer of knowledge to those we serve.",
  },
  {
    title: "Empowerment in Practice",
    body: "We believe professionals already have what it takes. Our role is to remove the noise and reveal the path forward.",
  },
];

const About = () => (
  <div data-testid="page-about">
    <PageHero
      overline="About"
      title="Meet Dr. Phelesia Foster, PhD"
      subtitle="Strategist. Coach. Mentor. Founder of The Exit Strategy Society."
      image={LIFESTYLE.executiveWoman}
    />

    {/* HER STORY */}
    <section className="ess-section bg-ess-paper">
      <div className="ess-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <img
            src={FOUNDER_PORTRAIT}
            alt="Dr. Phelesia Foster"
            className="w-full object-cover shadow-2xl"
          />
        </div>
        <div className="lg:col-span-7 lg:pt-6">
          <SectionHeader
            overline="Her Story"
            title="A federal career. A doctorate. A decision."
            subtitle="Dr. Phelesia Foster is an accomplished professional with more than 26 years of federal service experience, a PhD in Public Health (Epidemiology), and a passion for helping individuals create meaningful change in their personal and professional lives."
          />
          <div className="mt-6 space-y-5 text-ess-charcoal/85 leading-relaxed">
            <p>
              As Founder of The Exit Strategy Society, Dr. Foster helps busy
              professionals reclaim their time, improve productivity, build
              additional income opportunities, and create strategic pathways
              toward greater freedom and flexibility.
            </p>
            <p>
              Her work bridges public health, digital education, and coaching —
              with the goal of helping professionals design lives aligned with
              their values, not against them.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* WHY WORK WITH DR. FOSTER — CREDENTIALS */}
    <section className="ess-section bg-white" data-testid="about-credentials">
      <div className="ess-container">
        <SectionHeader
          overline="Credentials"
          title="Why Work With Dr. Phelesia Foster?"
          subtitle="The Exit Strategy Society is built upon decades of professional leadership, education, service, and personal development experience."
          align="center"
          maxWidth="max-w-3xl mx-auto"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ess-blush mt-14 border border-ess-blush">
          {credentials.map(({ icon: Icon, kicker, title }) => (
            <div
              key={title}
              className="bg-white p-7 group hover:bg-ess-paper transition-colors"
            >
              <div className="w-11 h-11 border border-ess-gold/40 flex items-center justify-center text-ess-gold-deep group-hover:bg-ess-emerald group-hover:text-ess-gold group-hover:border-ess-emerald transition-all">
                <Icon size={18} strokeWidth={1.4} />
              </div>
              <p className="font-display text-2xl text-ess-emerald mt-5 leading-tight">
                {kicker}
              </p>
              <p className="text-[13px] text-ess-charcoal/80 mt-2 leading-relaxed">
                {title}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* WHY ESS EXISTS — FOUNDER MESSAGE */}
    <section className="ess-section bg-ess-paper" data-testid="about-founder-message">
      <div className="ess-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 lg:order-2">
          <img
            src={FOUNDER_PORTRAIT_ALT}
            alt="Dr. Phelesia Foster"
            className="w-full aspect-[4/5] object-cover shadow-2xl"
          />
        </div>
        <div className="lg:col-span-7 lg:order-1">
          <p className="ess-overline">Why ESS Exists</p>
          <h2 className="font-display mt-4 text-4xl md:text-5xl text-ess-emerald leading-tight">
            A Message From Dr. Phelesia Foster
          </h2>
          <span className="ess-divider mt-6" />
          <div className="mt-8 space-y-5 text-ess-charcoal/85 leading-relaxed">
            <p>
              After more than 26 years of federal service, I realized many
              professionals were successful on paper but overwhelmed in practice.
            </p>
            <p>
              Too many talented individuals were navigating burnout, digital
              overload, financial stress, and uncertainty about their next chapter.
            </p>
            <p>
              The Exit Strategy Society was created to help professionals reclaim
              their time, increase their confidence, expand their opportunities,
              and build lives aligned with their values.
            </p>
            <p className="font-display text-2xl text-ess-emerald italic leading-snug">
              My mission is simple: to help people create more options, more
              freedom, and more fulfillment through education, strategy, and
              personal growth.
            </p>
            <p>This is not about escaping life.</p>
            <p className="font-display text-xl text-ess-emerald italic">
              This is about intentionally designing it.
            </p>
          </div>
          <div className="mt-8 border-l-2 border-ess-gold pl-4">
            <p className="font-display text-xl text-ess-emerald leading-tight">
              — Dr. Phelesia Foster, PhD
            </p>
            <p className="ess-overline mt-1">
              Founder, The Exit Strategy Society
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* MISSION / VISION */}
    <section className="ess-section bg-ess-blush">
      <div className="ess-container grid grid-cols-1 md:grid-cols-2 gap-px bg-ess-emerald/15 border border-ess-emerald/15">
        <div className="bg-ess-blush p-10 md:p-14">
          <p className="ess-overline">Mission</p>
          <h3 className="font-display text-3xl md:text-4xl mt-4 text-ess-emerald leading-tight">
            To empower professionals, particularly women, with the tools,
            education, strategies, and resources needed to reclaim their time,
            improve financial confidence, increase economic opportunity, and
            create sustainable pathways toward long-term freedom and fulfillment.
          </h3>
        </div>
        <div className="bg-ess-blush p-10 md:p-14">
          <p className="ess-overline">Vision</p>
          <h3 className="font-display text-3xl md:text-4xl mt-4 text-ess-emerald leading-tight">
            A future where professionals have the knowledge, flexibility,
            confidence, and opportunities needed to design lives aligned with
            their values and goals.
          </h3>
        </div>
      </div>
    </section>

    {/* VALUES */}
    <section className="ess-section bg-white">
      <div className="ess-container">
        <SectionHeader
          overline="Our Values"
          title="Three principles guide everything we build."
          align="center"
          maxWidth="max-w-2xl mx-auto"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ess-blush mt-14 border border-ess-blush">
          {values.map((v, i) => (
            <div key={v.title} className="bg-white p-10">
              <p className="font-display text-5xl text-ess-gold">0{i + 1}</p>
              <h3 className="font-display text-2xl mt-4 text-ess-emerald">{v.title}</h3>
              <p className="text-ess-slate mt-3 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="ess-section bg-ess-emerald text-white text-center">
      <div className="ess-container">
        <h2 className="font-display text-4xl md:text-5xl">
          Design your next chapter — with us.
        </h2>
        <div className="mt-8">
          <EmeraldButton
            as="link"
            to="/book"
            className="bg-ess-gold text-ess-charcoal hover:bg-white"
          >
            Book a Consultation <ArrowRight size={14} className="ml-3" />
          </EmeraldButton>
        </div>
      </div>
    </section>
  </div>
);

export default About;
